import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { api } from "../lib/api";
import AssetCard from "../components/AssetCard";
import { toast } from "sonner";
import { Trash2, Plus, ArrowUpRight } from "lucide-react";

export default function MyUploads() {
  const { user, loading } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);

  const fetchItems = () => {
    setBusy(true);
    api
      .get("/files/me")
      .then((r) => setItems(r.data.items || []))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    if (!loading) {
      if (!user) nav("/login");
      else fetchItems();
    }
  }, [user, loading, nav]);

  const remove = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/files/${id}`);
      toast.success("Deleted");
      setItems((arr) => arr.filter((x) => x.id !== id));
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <main className="min-h-[calc(100vh-72px)] py-14">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid grid-cols-1 items-end gap-6 lg:grid-cols-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-fg">§ — Workshop</div>
            <h1 className="font-display mt-3 text-5xl leading-[0.95] sm:text-6xl" data-testid="my-uploads-title">
              <span className="font-serif text-accent">My</span>{" "}
              <span className="text-ink">uploads.</span>
            </h1>
            <p className="mt-3 text-muted-fg">{t("myUploadsSub")}</p>
          </div>
          <div className="lg:text-end">
            <Link to="/upload" className="btn btn-accent" data-testid="my-uploads-new">
              <Plus className="h-4 w-4" />
              {t("upload")}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {busy ? (
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="aspect-[4/3] animate-pulse rounded-[var(--radius)] surface-2" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="mt-12 card p-16 text-center">
            <div className="font-display text-3xl text-muted-fg">{t("noUploads")}</div>
            <div className="mt-6">
              <Link to="/upload" className="btn btn-accent">
                {t("upload")}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {items.map((it) => (
              <div key={it.id} className="relative">
                <AssetCard item={it} />
                <button
                  onClick={() => remove(it.id)}
                  className="absolute end-3 bottom-3 z-10 rounded-full bg-[hsl(var(--ink))] p-2 text-[hsl(var(--bg))] shadow-md transition-colors hover:bg-[hsl(var(--accent))]"
                  data-testid={`delete-file-${it.id}`}
                  title={t("delete")}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
